import { lazy, type ComponentType } from 'react'

const RELOADED_AT_KEY = 'chunk-reload-at'

/**
 * React.lazy that survives deploys. Every deploy replaces the content-hashed
 * chunk files, so a tab opened before the deploy 404s when it lazy-loads a
 * route it hasn't visited yet — the import throws and, with no error boundary,
 * React unmounts to a white page. On a failed chunk load this reloads the page
 * once (per minute, via sessionStorage) so the browser picks up the fresh
 * bundle; if the import still fails after a reload, the error propagates.
 */
// Same constraint as React.lazy, so components that take props fit too.
export function lazyWithReload<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    load().catch((err: unknown) => {
      let recentlyReloaded = true
      try {
        const last = Number(sessionStorage.getItem(RELOADED_AT_KEY) ?? 0)
        recentlyReloaded = Date.now() - last < 60_000
        if (!recentlyReloaded) sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()))
      } catch { /* storage unavailable - fall through to throwing */ }

      if (recentlyReloaded) throw err
      window.location.reload()
      // Keep Suspense pending while the reload happens.
      return new Promise<{ default: T }>(() => {})
    }),
  )
}
